import { categories } from 'constants/tests';

/**
 * Given the raw description transform it into the text to display
 *
 * @param {*} description The description text
 * @param {*} category Test category
 * @param {*} language code language
 * @param {*} levelToRun exercise levelToRun
 * @param {*} appLanguage The browser/app language
 * @param {*} isCourse bool indicating if description is part of course page
 * @param {*} isFlow bool indicating to not remove the flow custom tags
 * @returns the computed description to display
 */
export default function getComputedDescription(
  description,
  category,
  language,
  levelToRun,
  appLanguage,
  isCourse,
  isFlow
) {
  let level = levelToRun + 1;
  let computedDescription = description || '';
  if (computedDescription) {
    computedDescription = getDescriptionWithPreservedCourseTags(computedDescription, isCourse);
    computedDescription = getDescriptionWithPreservedAppLanguageTags(computedDescription, appLanguage);
    computedDescription = getDescriptionWithSwitchOptions(computedDescription, category, language, level);
    computedDescription = getTranslatedDescription(computedDescription, category, language, level);
    computedDescription = getDescriptionWithPreservedTagContent(computedDescription, category, language, level);
    computedDescription = getDescriptionClearedFromTags(computedDescription, category, language, level, isFlow);
    computedDescription = wrapEmbeddedVideosInDiv(computedDescription);
  }
  return computedDescription;
}

/** Escape regex characters in the string so it can be used inside a regex */
const escapeRegExp = (stringToEscape) => stringToEscape.replace(/[.*+\-?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string

const getDescriptionWithPreservedCourseTags = (description, isCourse) => {
  if (!isCourse) return description;

  // This will remove all tags for course while preserving content
  // If the course tags are not removed then getDescriptionClearedFromTags will remove them and their content
  const startTag = `[course]`;
  const endTag = `[/course]`;
  while (true) {
    let startTagIndex = description.indexOf(startTag);
    let endTagIndex = description.indexOf(endTag, startTagIndex + startTag.length);
    if (startTagIndex < 0 || endTagIndex < 0) break;
    description =
      description.substring(0, startTagIndex) +
      description.substring(startTagIndex + startTag.length, endTagIndex) +
      description.substring(endTagIndex + endTag.length);
  }
  return description;
};

// Map appLanguage to more verbose tag languages
const tagLanguages = {
  sv: 'swedish',
  en: 'english',
  de: 'german',
};
const getDescriptionWithPreservedAppLanguageTags = (description, appLanguage) => {
  let languageToPreserve = tagLanguages[appLanguage] || tagLanguages.en;
  if (
    languageToPreserve !== tagLanguages.en &&
    !description.includes(`[/${languageToPreserve}]`) &&
    description.includes(`[/${tagLanguages.en}]`)
  ) {
    // If there are english tags but no tags for selected language, then preserve english instead
    languageToPreserve = tagLanguages.en;
  }

  // This will remove all tags for languageToPreserve while preserving content
  // Other languages will be removed in getDescriptionClearedFromTags
  const startTag = `[${languageToPreserve}]`;
  const endTag = `[/${languageToPreserve}]`;
  while (true) {
    let startTagIndex = description.indexOf(startTag);
    let endTagIndex = description.indexOf(endTag, startTagIndex + startTag.length);
    if (startTagIndex < 0 || endTagIndex < 0) break;
    description =
      description.substring(0, startTagIndex) +
      description.substring(startTagIndex + startTag.length, endTagIndex) +
      description.substring(endTagIndex + endTag.length);
  }
  return description;
};

const getDescriptionWithSwitchOptions = (description, category, language, level) => {
  // Example: [switch][python]and[/python][default]&&[/default][/switch]
  // Need to use closing tags to handle line breaks and styling since description is html
  let startTag = '[switch]';
  let endTag = '[/switch]';

  let matchedStartTags = description.matchAll(/\[switch\]/g);
  let startTagMatch = matchedStartTags.next();
  while (!startTagMatch.done) {
    let startTagIndex = description.indexOf(startTag);
    let endTagIndex = description.indexOf(endTag, startTagIndex + startTag.length);
    if (startTagIndex >= 0 && startTagIndex < endTagIndex) {
      // replace the switch with desired content
      description =
        description.substring(0, startTagIndex) +
        getDescriptionSwitchContent(
          description.substring(startTagIndex + startTag.length, endTagIndex),
          category,
          language,
          level
        ) +
        description.substring(endTagIndex + endTag.length);
    }
    startTagMatch = matchedStartTags.next();
  }
  return description;
};

const getDescriptionSwitchContent = (switchContent, category, language, level) => {
  // Get the first match from switchContent or try to find[default][/default]
  let matchedStartTags;
  if (category === categories.EXERCISE.category && level) {
    // If exercise match on level or language
    let levelTag = 'level' + level;
    matchedStartTags = switchContent.matchAll(
      new RegExp('\\[(\\w+\\|)*(' + language + '|' + levelTag + ')(\\|\\w+)*\\]', 'g')
    );
  } else {
    // match only on language
    matchedStartTags = switchContent.matchAll(new RegExp('\\[(\\w+\\|)*' + language + '(\\|\\w+)*\\]', 'g'));
  }
  let startTagMatch = matchedStartTags.next();
  while (!startTagMatch.done) {
    let startTag = startTagMatch.value[0];
    let endTag = startTag.replace(/^\[/, '[/');
    let startTagIndex = switchContent.indexOf(startTag);
    let endTagIndex = switchContent.indexOf(endTag, startTagIndex + startTag.length);
    if (startTagIndex >= 0 && startTagIndex < endTagIndex) {
      // tag match found, return it
      return switchContent.substring(startTagIndex + startTag.length, endTagIndex);
    }
    startTagMatch = matchedStartTags.next();
  }

  // No specific tag match found, try to find default
  let startTag = '[default]';
  let endTag = '[/default]';
  let startTagIndex = switchContent.indexOf(startTag);
  let endTagIndex = switchContent.indexOf(endTag, startTagIndex + startTag.length);
  if (startTagIndex >= 0 && startTagIndex < endTagIndex) {
    // default content found, return it
    return switchContent.substring(startTagIndex + startTag.length, endTagIndex);
  }

  return '';
};

const getTranslatedDescription = (description, category, language, level) => {
  // Find all [translate] tags, parse content, use them to update the description
  let startTag = '[translate]';
  let endTag = '[/translate]';
  let matchedStartTags = description.matchAll(/\[translate\]/g);
  let startTagMatch = matchedStartTags.next();
  while (!startTagMatch.done) {
    let startTagIndex = description.indexOf(startTag);
    let endTagIndex = description.indexOf(endTag, startTagIndex + startTag.length);
    if (startTagIndex >= 0 && startTagIndex < endTagIndex) {
      // Get parsed content with from and to
      let translationDict = getTranslatedContent(
        description.substring(startTagIndex + startTag.length, endTagIndex),
        category,
        language,
        level
      );
      // Update the description
      description = description.substring(0, startTagIndex) + description.substring(endTagIndex + endTag.length);
      if (translationDict.from) {
        description = description.replaceAll('*' + translationDict.from, translationDict.to);
      }
    }

    startTagMatch = matchedStartTags.next();
  }
  return description;
};

const getTranslatedContent = (translateContent, category, language, level) => {
  let result = {};
  // Find what to translate from
  let startTag = '[from]';
  let endTag = '[/from]';
  let startTagIndex = translateContent.indexOf(startTag);
  let endTagIndex = translateContent.indexOf(endTag, startTagIndex + startTag.length);
  if (startTagIndex >= 0 && startTagIndex < endTagIndex) {
    result.from = translateContent.substring(startTagIndex + startTag.length, endTagIndex);
  }
  // Get what to translate to from switch content function
  result.to = getDescriptionSwitchContent(translateContent, category, language, level);
  return result;
};

const getDescriptionWithPreservedTagContent = (description, category, language, level) => {
  // Determine what leftover tag values we might want to preserve
  let tagsToPreserve = [language];
  if (category === categories.EXERCISE.category && level) {
    tagsToPreserve.push('level' + level);
  }
  for (let i = 0; i < tagsToPreserve.length; i++) {
    // match all tags like [python] or [python|cpp] etc if you are using python
    let matchedStartTags = description.matchAll(
      new RegExp('\\[(\\w+\\|)*' + escapeRegExp(tagsToPreserve[i]) + '(\\|\\w+)*\\]', 'g')
    );
    let startTagMatch = matchedStartTags.next();
    while (!startTagMatch.done) {
      let startTag = startTagMatch.value[0];
      let endTag = startTag.replace(/^\[/, '[/');
      let startTagIndex = description.indexOf(startTag);
      let endTagIndex = description.indexOf(endTag, startTagIndex + startTag.length);
      if (startTagIndex >= 0 && startTagIndex < endTagIndex) {
        // remove the tags but keep content
        description =
          description.substring(0, startTagIndex) +
          description.substring(startTagIndex + startTag.length, endTagIndex) +
          description.substring(endTagIndex + endTag.length);
      }

      startTagMatch = matchedStartTags.next();
    }
  }
  return description;
};

// Regex that should find all start tags that are not used from flow
const allNoneFlowTags =
  /\[(?!(quiz|\/quiz|question|\/question|option|\/option|option-correct|\/option-correct|feedback-correct|\/feedback-correct|feedback-incorrect|\/feedback-incorrect|hint|\/hint|explanation|\/explanation|continue|coding|\/coding|interact-\d+)\])[^\]]+\]/g;

// Regex to find all start tags
const allLeftoverTags = /\[(\w+\|)*\w+\]/g;

const getDescriptionClearedFromTags = (description, isFlow) => {
  // Match all leftover tags and remove them and their content if they are closed
  // Opening tag examples: [python], [python|ruby], [level1|level2|level6]
  // If isFlow will preserve flow tags

  let matchedStartTags = description.matchAll(isFlow ? allNoneFlowTags : allLeftoverTags);

  let startTagMatch = matchedStartTags.next();
  while (!startTagMatch.done) {
    let startTag = startTagMatch.value[0];
    let endTag = startTag.replace(/^\[/, '[/');
    let startTagIndex = description.indexOf(startTag);
    let endTagIndex = description.indexOf(endTag, startTagIndex + startTag.length);
    if (startTagIndex >= 0 && startTagIndex < endTagIndex) {
      // remove the tags and their content
      description = description.substring(0, startTagIndex) + description.substring(endTagIndex + endTag.length);
    }
    startTagMatch = matchedStartTags.next();
  }
  return description;
};

const wrapEmbeddedVideosInDiv = (description) => {
  // Programatically update displayed html to wrap iframes for dynamic size with correct height
  const startTag = '<iframe ';
  const endTag = '</iframe>';
  let startIndex = 0;
  let endIndex = 0;

  while (true) {
    startIndex = description.indexOf(startTag, endIndex);
    endIndex = description.indexOf(endTag, startIndex) + endTag.length;
    if (startIndex < 0 || endIndex <= startIndex) break;
    description =
      description.substring(0, startIndex) +
      '<div class="custom-video-div">' +
      description.substring(startIndex, endIndex) +
      '</div>' +
      description.substring(endIndex);
  }
  return description;
};
